import { combineErrors } from './combineErrors';
import { ErrorType, Validator } from './types';

const ERROR_REQUIRED_FIELD = 'Обязательное поле';
const ERROR_INCORRECT_FIELD = 'Допустимы только буквы русского алфавита';
const ERROR_INCORRECT_URL = 'Некорректный URL';
const ERROR_INCORRECT_EMAIL = 'Некорректный Email';
const ERROR_MAX_LENGTH = 'Превышена максимальная длина';
const ERROR_PHONE = 'Некорректный номер телефона';
const ERROR_ONLY_LETTERS = 'Допустимы только буквы русского алфавита';
const ERROR_INN = 'Некорректный инн';
const ERROR_DATE_FORMAT = 'Введите название в формате YYYY-MM-DD';
const PASSWORD_SPECIALS = new Set('!@#$%^&*()_+-=[]{}|;:,.<>?/`~"\'\\'.split(''));

export const getEmptyError = (): ErrorType => {
  return { value: false };
};

export const getError = (text: string): ErrorType => {
  return { value: true, text };
};

export const validateNotEmptyArray =
  <T>(error: string = ERROR_REQUIRED_FIELD): Validator<T[]> =>
  (value: T[]): ErrorType =>
    !value.length ? getError(error) : getEmptyError();

export const validateMaxArrayLength =
  <T>(maxLength: number, error: string = `Можно выбрать не более ${maxLength}`): Validator<T[]> =>
  (value: T[]): ErrorType =>
    value.length > maxLength ? getError(error) : getEmptyError();

export const validateNotEmpty =
  <T>(error: string = ERROR_REQUIRED_FIELD): Validator<T> =>
  (value?: T | null): ErrorType =>
    value === undefined || value === null || !String(value).trim().length ? getError(error) : getEmptyError();

export const validateMinLength =
  <T>(minLength: number, error: string = `Минимум ${minLength} символов`): Validator<T> =>
  (value?: T | null): ErrorType =>
    value === undefined || value === null || String(value).trim().length < minLength
      ? getError(error)
      : getEmptyError();

export const validateMaxLength =
  <T>(maxLength: number, error: string = ERROR_MAX_LENGTH): Validator<T> =>
  (value?: T | null): ErrorType =>
    value === undefined || value === null || String(value).trim().length > maxLength
      ? getError(error)
      : getEmptyError();

export const validateEmail =
  <T>(errorText: string = ERROR_INCORRECT_EMAIL): Validator<T> =>
  (v?: T | null): ErrorType =>
    !/^[a-zA-Z0-9_.+-]+@[a-zA-Z0-9-]+\.[a-zA-Z0-9-.]+$/.test(String(v)) ? getError(errorText) : getEmptyError();

/** Валидирует через все валидаторы и возвращает все ошибки */
export const composeValidators =
  <V>(...funcs: Array<Validator<V>>): Validator<V> =>
  (v: V): ErrorType =>
    funcs.reduce<ErrorType>((acc, validator) => combineErrors(acc, validator(v)), getEmptyError());

/** Валидирует поочередно и возвращает первую ошибку */
export const orderValidators =
  <V>(...funcs: Array<Validator<V>>): Validator<V> =>
  (v: V): ErrorType => {
    for (const validator of funcs) {
      const result = validator(v);

      if (result.value) {
        return result;
      }
    }

    return getEmptyError();
  };

/** Валидирует поле если выполнено условие condition */
export const validateIf =
  <T>(
    condition: (value: T) => boolean, // условие, при котором мы проходим валидацию
    validator: Validator<T>,
  ): Validator<T> =>
  (value: T): ErrorType => {
    if (condition(value)) {
      return validator(value);
    }

    return { value: false };
  };

/** Произвольный валидатор */
export const customValidate =
  <T>(condition: (v?: T) => boolean, error: string | ((v?: T) => string) = ERROR_INCORRECT_FIELD): Validator<T> =>
  (v?: T): ErrorType => {
    const errorText = typeof error === 'function' ? error(v) : error;

    return condition(v)
      ? getEmptyError()
      : {
          value: true,
          text: [errorText],
        };
  };

export const validatePassword: Validator<string | null> = orderValidators<string | null>(
  validateMinLength(8, 'Минимум 8 символов'),
  validateMaxLength(128),
  customValidate((v) => /\p{L}/u.test(String(v)), 'Пароль должен содержать хотя бы одну букву'),
  customValidate((v) => /\d/.test(String(v)), 'Пароль должен содержать хотя бы одну цифру'),
  customValidate(
    (v) => [...String(v)].some((symbol) => PASSWORD_SPECIALS.has(symbol)),
    'Пароль должен содержать хотя бы один спецсимвол',
  ),
);

export const validateNumber =
  <T>(errorText: string = ERROR_INCORRECT_FIELD): Validator<T> =>
  (v?: T | null): ErrorType =>
    /[^0-9]/.test(String(v))
      ? {
          value: true,
          text: [errorText],
        }
      : getEmptyError();

export const validateLetters =
  <T>(errorText: string = ERROR_ONLY_LETTERS): Validator<T> =>
  (v?: T | null): ErrorType =>
    v && /[^А-Яа-я]/.test(String(v))
      ? {
          value: true,
          text: [errorText],
        }
      : getEmptyError();

export const validateUrl =
  <T>(errorText: string = ERROR_INCORRECT_URL): Validator<T> =>
  (v?: T | null): ErrorType =>
    /^https?:\/\/[^\s/$.?#].[^\s]*$/.test(String(v))
      ? getEmptyError()
      : {
          value: true,
          text: [errorText],
        };

export const validatePhoneNumber =
  <T>(errorText: string = ERROR_PHONE): Validator<T> =>
  (v?: T | null): ErrorType => {
    const formattedValue = String(v).replace(/\D+/g, '');

    return /^\d{11}$/.test(formattedValue) ? getEmptyError() : getError(errorText);
  };

export const validateInn =
  <T>(errorText: string = ERROR_INN): Validator<T> =>
  (v?: T | null): ErrorType => {
    return /^\d{10}(\d{2})?$/.test(String(v)) ? getEmptyError() : getError(errorText);
  };

export const validateDateYyyyMmDd =
  <T>(errorText: string = ERROR_DATE_FORMAT): Validator<T> =>
  (value?: T | null): ErrorType => {
    const stringValue = value === undefined || value === null ? '' : String(value).trim();

    if (!stringValue) {
      return getError(errorText);
    }

    const match = stringValue.match(/^(\d{4})-(\d{2})-(\d{2})$/);

    if (!match) {
      return getError(errorText);
    }

    const [, yearString, monthString, dayString] = match;
    const year = Number(yearString);
    const month = Number(monthString);
    const day = Number(dayString);

    if (Number.isNaN(year) || year < 2000 || year > 2100) {
      return getError(errorText);
    }

    if (Number.isNaN(month) || month < 1 || month > 12) {
      return getError(errorText);
    }

    if (Number.isNaN(day) || day < 1 || day > 31) {
      return getError(errorText);
    }

    return getEmptyError();
  };
